/**
 * Session-review helpers (M0.3): default subscription = active sessions + last
 * 7 days, progress/duration derivation, cross-session activity feed and
 * incremental tail merging.
 *
 * Everything here is pure so it can be unit-tested without React or a daemon;
 * the real `/sessions` + `/sessions/:source/:id/tail` bridge feeds the same
 * view model (see docs/PROTOCOL-v0.md §8).
 */
import type { SessionSource, SessionStatus } from '@luminis/protocol';
import type { SessionEventKind, SessionEventView, SessionView } from './types';

export const SESSION_WINDOW_DAYS = 7;
export const SESSION_DAY_SECONDS = 86_400;
/** Upper bound kept per session in memory (old events roll off the detail view). */
export const MAX_SESSION_EVENTS = 400;
/** Upper bound for the merged activity feed. */
export const MAX_ACTIVITY_ITEMS = 80;

export const SESSION_SOURCE_LABELS: Record<SessionSource, string> = {
  dsh: 'DSH',
  codex: 'Codex',
  opencode: 'OpenCode',
  vscode: 'VS Code',
  luminis: 'Luminis',
};

export const SESSION_STATUS_LABELS: Record<SessionStatus, string> = {
  active: '进行中',
  idle: '空闲',
  done: '已完成',
  error: '异常',
};

/** Tailwind tone classes shared by the list, detail and activity feed. */
export const SESSION_STATUS_TONES: Record<SessionStatus, string> = {
  active: 'border-emerald-400/40 bg-emerald-400/10 text-emerald-200',
  idle: 'border-amber-400/30 bg-amber-400/10 text-amber-200',
  done: 'border-slate-500/30 bg-slate-500/10 text-slate-300',
  error: 'border-rose-400/40 bg-rose-400/10 text-rose-200',
};

export const SESSION_SOURCE_TONES: Record<SessionSource, string> = {
  dsh: 'bg-indigo-400/10 text-indigo-200',
  codex: 'bg-emerald-400/10 text-emerald-200',
  opencode: 'bg-sky-400/10 text-sky-200',
  vscode: 'bg-violet-400/10 text-violet-200',
  luminis: 'bg-amber-400/10 text-amber-200',
};

export const SESSION_EVENT_KIND_LABELS: Record<SessionEventKind, string> = {
  message: '消息',
  tool: '工具',
  status: '状态',
  file: '文件',
  progress: '进度',
};

export const SESSION_EVENT_KIND_TONES: Record<SessionEventKind, string> = {
  message: 'border-indigo-400/30 bg-indigo-400/10 text-indigo-200',
  tool: 'border-sky-400/30 bg-sky-400/10 text-sky-200',
  status: 'border-slate-500/30 bg-slate-500/10 text-slate-300',
  file: 'border-violet-400/30 bg-violet-400/10 text-violet-200',
  progress: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200',
};

export function sessionWindowStart(now: number, days = SESSION_WINDOW_DAYS): number {
  return now - Math.max(1, days) * SESSION_DAY_SECONDS;
}

/** Stable identity for a session across the protocol's (source, session_id) pair. */
export function sessionKey(session: { source: SessionSource; session_id: string }): string {
  return `${session.source}\u0000${session.session_id}`;
}

/**
 * Default subscription rule (docs/PLAN.md §16.5): sessions that are currently
 * active are always kept; everything else is kept only when it had activity
 * within the last `days` days.
 */
export function isSessionVisible(
  session: SessionView,
  now: number,
  days = SESSION_WINDOW_DAYS,
): boolean {
  if (session.status === 'active') return true;
  return session.last_event_at >= sessionWindowStart(now, days);
}

/**
 * Deterministic Unicode code-point comparison (plain `<`/`>`).
 *
 * Deliberately not `localeCompare`: the PWA must sort identically in Node tests
 * and every browser regardless of the user's locale/ICU version.
 */
function compareText(left: string, right: string): number {
  if (left === right) return 0;
  return left < right ? -1 : 1;
}

/** Title used for ordering; blank titles get a stable minimum placeholder. */
function sessionSortTitle(session: SessionView): string {
  const title = session.title?.trim() ?? '';
  return title.length > 0 ? title : '\u0000';
}

/**
 * Session ordering: source ascending → title ascending (Unicode code points) →
 * session_id ascending. Active status/last_event_at do not influence the order
 * anymore; the “进行中 / 最近 7 天” split is a UI grouping only.
 */
export function compareSessionOrder(left: SessionView, right: SessionView): number {
  const bySource = compareText(left.source, right.source);
  if (bySource !== 0) return bySource;
  const byTitle = compareText(sessionSortTitle(left), sessionSortTitle(right));
  if (byTitle !== 0) return byTitle;
  return compareText(left.session_id, right.session_id);
}

/** Source → title → session_id ordering (deterministic, see compareSessionOrder). */
export function sortSessions(sessions: readonly SessionView[]): SessionView[] {
  return [...sessions].sort(compareSessionOrder);
}

export interface SessionTreeNode {
  session: SessionView;
  /** `sessionKey(session)`: stable across the tree/detail/activity views. */
  key: string;
  children: SessionTreeNode[];
  /** All descendants (recursive), used for the “N 个分任务” badge. */
  descendantCount: number;
  /** 0 for roots; children are depth+1. */
  depth: number;
}

export interface SessionTree {
  /** Sessions without a visible parent (also orphans/cycle-broken), sorted. */
  roots: SessionTreeNode[];
  /** Direct children of each root keyed by `sessionKey(root)`. */
  childrenByRoot: Map<string, SessionTreeNode[]>;
  /** Every input session keyed by `sessionKey` (deduped, cycle-safe). */
  nodesByKey: Map<string, SessionTreeNode>;
}

/**
 * Build the “main task → subtasks” hierarchy (M0.3, best-effort).
 *
 * `parent_session_id` is an additive `SessionSummary.meta` field sent by the
 * daemon; links never cross sources (a `dsh` parent cannot own a `codex` child).
 * Sessions whose parent is missing (hidden by the 7-day window, filtered by
 * source, or not reported) stay roots (orphan → root). Self references and
 * multi-node cycles are broken deterministically (first node in input order
 * becomes the root) so the tree is always finite and every session appears once.
 *
 * Roots and children are ordered with the same active-first/latest rule as the
 * flat list; the input is treated as the visible set.
 */
export function buildSessionTree(sessions: readonly SessionView[]): SessionTree {
  const nodesByKey = new Map<string, SessionTreeNode>();
  for (const session of sessions) {
    const key = sessionKey(session);
    if (nodesByKey.has(key)) continue;
    nodesByKey.set(key, {
      session,
      key,
      children: [],
      descendantCount: 0,
      depth: 0,
    });
  }

  const parentByKey = new Map<string, string>();
  for (const node of nodesByKey.values()) {
    const parentId = node.session.parent_session_id?.trim();
    if (!parentId) continue;
    const parentKey = `${node.session.source}\u0000${parentId}`;
    if (parentKey === node.key) continue; // self reference → root
    if (!nodesByKey.has(parentKey)) continue; // orphan / other source → root
    parentByKey.set(node.key, parentKey);
  }

  // Deterministic cycle breaking: walk each parent chain; the first node (input
  // order) that detects a cycle drops its own edge and becomes a root.
  for (const node of nodesByKey.values()) {
    let current = parentByKey.get(node.key);
    if (current === undefined) continue;
    const seen = new Set<string>([node.key]);
    let cycle = false;
    while (current !== undefined) {
      if (seen.has(current)) {
        cycle = true;
        break;
      }
      seen.add(current);
      current = parentByKey.get(current);
    }
    if (cycle) parentByKey.delete(node.key);
  }

  const roots: SessionTreeNode[] = [];
  for (const node of nodesByKey.values()) {
    const parentKey = parentByKey.get(node.key);
    const parent = parentKey !== undefined ? nodesByKey.get(parentKey) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }

  roots.sort((left, right) => compareSessionOrder(left.session, right.session));

  const assign = (node: SessionTreeNode, depth: number): number => {
    node.depth = depth;
    node.children.sort((left, right) => compareSessionOrder(left.session, right.session));
    let total = 0;
    for (const child of node.children) total += 1 + assign(child, depth + 1);
    node.descendantCount = total;
    return total;
  };
  for (const root of roots) assign(root, 0);

  const childrenByRoot = new Map<string, SessionTreeNode[]>();
  for (const root of roots) childrenByRoot.set(root.key, root.children);
  return { roots, childrenByRoot, nodesByKey };
}

export interface SessionTreeRow {
  node: SessionTreeNode;
  session: SessionView;
  /** Indentation depth (root = 0). */
  depth: number;
  /** Whether this row's children are currently rendered. */
  expanded: boolean;
}

/**
 * Flatten one tree section into render rows. With no expanded keys only roots are
 * returned (default “main tasks only” view); `expandAll` is the optional
 * “show all subtasks” debug switch.
 */
export function flattenSessionTree(
  roots: readonly SessionTreeNode[],
  expandedKeys: ReadonlySet<string>,
  options: { expandAll?: boolean } = {},
): SessionTreeRow[] {
  const rows: SessionTreeRow[] = [];
  const walk = (node: SessionTreeNode): void => {
    const expanded = options.expandAll === true || expandedKeys.has(node.key);
    rows.push({ node, session: node.session, depth: node.depth, expanded });
    if (!expanded) return;
    for (const child of node.children) walk(child);
  };
  for (const root of roots) walk(root);
  return rows;
}

// ---------------------------------------------------------------------------
// Task-28: source/status filter chips + hierarchy-aware counts
// ---------------------------------------------------------------------------

/** Status chip groups: active / done / other (idle + error). */
export type SessionStatusFilter = 'active' | 'done' | 'other';

export const SESSION_STATUS_FILTER_LABELS: Record<SessionStatusFilter, string> = {
  active: '进行中',
  done: '已完成',
  other: '其他',
};

export interface SessionFilters {
  source: SessionSource | null;
  status: SessionStatusFilter | null;
}

export const SESSION_FILTERS_NONE: SessionFilters = { source: null, status: null };

export function statusMatchesFilter(
  status: SessionStatus,
  filter: SessionStatusFilter | null,
): boolean {
  if (filter === null) return true;
  if (filter === 'active') return status === 'active';
  if (filter === 'done') return status === 'done';
  return status === 'idle' || status === 'error';
}

export function sessionMatchesFilters(session: SessionView, filters: SessionFilters): boolean {
  if (filters.source !== null && session.source !== filters.source) return false;
  return statusMatchesFilter(session.status, filters.status);
}

/**
 * Apply source/status chips to the visible set. The filtered list is then fed to
 * `buildSessionTree`, so a filtered-out parent leaves its children as roots
 * (best-effort: children are never hidden just because the parent was filtered).
 */
export function filterSessions(
  sessions: readonly SessionView[],
  filters: SessionFilters,
): SessionView[] {
  if (filters.source === null && filters.status === null) return [...sessions];
  return sessions.filter((session) => sessionMatchesFilters(session, filters));
}

export interface SessionFilterCounts {
  /** Visible sessions before filtering (chip “全部”). */
  all: number;
  /** Count per source among visible sessions (only present sources matter). */
  bySource: Map<SessionSource, number>;
  byStatus: { active: number; done: number; other: number };
}

/** Chip counts are computed over the visible set, independent of the selection. */
export function sessionFilterCounts(sessions: readonly SessionView[]): SessionFilterCounts {
  const bySource = new Map<SessionSource, number>();
  const byStatus = { active: 0, done: 0, other: 0 };
  for (const session of sessions) {
    bySource.set(session.source, (bySource.get(session.source) ?? 0) + 1);
    if (session.status === 'active') byStatus.active += 1;
    else if (session.status === 'done') byStatus.done += 1;
    else byStatus.other += 1;
  }
  return { all: sessions.length, bySource, byStatus };
}

export interface SessionHierarchyCounts {
  /** Currently active sessions in the filtered set. */
  active: number;
  /** Current tree roots (main tasks after filtering). */
  roots: number;
  /** Filtered visible sessions (roots + subtasks). */
  visible: number;
  /** Visible sessions that are not roots (best-effort after filtering). */
  subtasks: number;
}

/**
 * Hierarchy-aware top-line numbers, e.g. “进行中 2 · 主任务 12 · 可见 61
 * （含 49 分任务）”. `tree` must be built from the same (filtered) sessions;
 * otherwise it is derived here.
 */
export function sessionHierarchyCounts(
  sessions: readonly SessionView[],
  tree: SessionTree = buildSessionTree(sessions),
): SessionHierarchyCounts {
  return {
    active: sessions.filter((session) => session.status === 'active').length,
    roots: tree.roots.length,
    visible: sessions.length,
    subtasks: Math.max(0, sessions.length - tree.roots.length),
  };
}

export interface SessionGroups {
  /** Running sessions (status `active`), regardless of age. */
  active: SessionView[];
  /** Everything else updated within the window, newest first. */
  recent: SessionView[];
  /** In-window count (active + recent). */
  visibleCount: number;
  /** Older, non-active sessions hidden by the default 7-day window. */
  hiddenCount: number;
}

export function groupSessions(
  sessions: readonly SessionView[],
  now: number,
  days = SESSION_WINDOW_DAYS,
): SessionGroups {
  const visible = sessions.filter((session) => isSessionVisible(session, now, days));
  const sorted = sortSessions(visible);
  const active = sorted.filter((session) => session.status === 'active');
  const recent = sorted.filter((session) => session.status !== 'active');
  return {
    active,
    recent,
    visibleCount: sorted.length,
    hiddenCount: sessions.length - sorted.length,
  };
}

/** Start time used for the “耗时” metric: explicit start, else first event. */
export function sessionStartedAt(session: SessionView): number {
  if (typeof session.started_at === 'number' && Number.isFinite(session.started_at)) {
    return Math.min(session.started_at, session.last_event_at);
  }
  const firstEvent = session.events[0];
  if (firstEvent && Number.isFinite(firstEvent.ts))
    return Math.min(firstEvent.ts, session.last_event_at);
  return session.last_event_at;
}

/**
 * Elapsed wall-clock duration. Active sessions keep counting up to `now`;
 * finished/idle sessions stop at their last event.
 */
export function sessionDurationSeconds(session: SessionView, now: number): number {
  const start = sessionStartedAt(session);
  const end =
    session.status === 'active' ? Math.max(now, session.last_event_at) : session.last_event_at;
  return Math.max(0, Math.round(end - start));
}

/** Reported progress 0–100; completed sessions read as 100, unknown as null. */
export function sessionProgress(session: SessionView): number | null {
  if (typeof session.progress === 'number' && Number.isFinite(session.progress)) {
    return Math.min(100, Math.max(0, Math.round(session.progress)));
  }
  if (session.status === 'done') return 100;
  return null;
}

export function sessionPhase(session: SessionView): string | null {
  const phase = session.phase?.trim();
  return phase ? phase : null;
}

export function eventKey(event: SessionEventView): string {
  return [event.ts, event.kind, event.role ?? '', event.tool ?? '', event.text ?? ''].join(
    '\u0000',
  );
}

/**
 * Incremental tail merge: append unseen events (dedupe on the unified event
 * shape), keep chronological order and bound memory. Used by the live detail
 * view while `/sessions/.../tail` pages arrive.
 */
export function mergeSessionEvents(
  existing: readonly SessionEventView[],
  incoming: readonly SessionEventView[],
  limit = MAX_SESSION_EVENTS,
): SessionEventView[] {
  const seen = new Set(existing.map(eventKey));
  const merged = [...existing];
  for (const event of incoming) {
    const key = eventKey(event);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(event);
  }
  merged.sort((left, right) => left.ts - right.ts);
  return merged.length > limit ? merged.slice(merged.length - limit) : merged;
}

export interface ActivityItem {
  /** Stable React key (source/session/index/event). */
  key: string;
  source: SessionSource;
  session_id: string;
  session_title: string;
  session_status: SessionStatus;
  event: SessionEventView;
}

/**
 * Unified activity feed: every event from every in-window session in one
 * reverse-chronological stream (M0.3 “活动流”).
 */
export function buildActivityFeed(
  sessions: readonly SessionView[],
  limit = MAX_ACTIVITY_ITEMS,
): ActivityItem[] {
  const items: ActivityItem[] = [];
  for (const session of sessions) {
    session.events.forEach((event, index) => {
      items.push({
        key: `${session.source}\u0000${session.session_id}\u0000${index}\u0000${eventKey(event)}`,
        source: session.source,
        session_id: session.session_id,
        session_title: session.title,
        session_status: session.status,
        event,
      });
    });
  }
  items.sort(
    (left, right) =>
      right.event.ts - left.event.ts ||
      left.source.localeCompare(right.source) ||
      left.session_id.localeCompare(right.session_id) ||
      left.key.localeCompare(right.key),
  );
  return items.length > limit ? items.slice(0, limit) : items;
}

export interface SessionTotals {
  visibleCount: number;
  activeCount: number;
  tokens: number;
  costCny: number;
}

/** Aggregate token/cost numbers for the review header cards. */
export function sessionTotals(sessions: readonly SessionView[]): SessionTotals {
  return sessions.reduce<SessionTotals>(
    (totals, session) => ({
      visibleCount: totals.visibleCount + 1,
      activeCount: totals.activeCount + (session.status === 'active' ? 1 : 0),
      tokens: totals.tokens + (session.tokens ?? 0),
      // Round each addition to avoid 0.1+0.05 float noise in the UI/test.
      costCny: Number((totals.costCny + (session.cost_cny ?? 0)).toFixed(6)),
    }),
    { visibleCount: 0, activeCount: 0, tokens: 0, costCny: 0 },
  );
}

/** Latest event of a session, used for the one-line preview on the card. */
export function latestSessionEvent(session: SessionView): SessionEventView | null {
  return session.events.length > 0 ? (session.events[session.events.length - 1] ?? null) : null;
}
